import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import Pagination from "./Pagination";
import PriceRangePicker from "./PriceRangePicker";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";
import "../styles/global.css";
import { getDoctorRatingSummary } from "../utils/doctorReviews";

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENTS — copied 1:1 from FrontClinicList pattern
───────────────────────────────────────────────────────────── */

const SkeletonCard = () => (
  <li className="category-block">
    <div className="skeleton-card">
      <div className="skeleton-element skeleton-avatar"></div>
      <div style={{ flex: 1 }}>
        <div className="skeleton-element skeleton-line w-60"></div>
        <div className="skeleton-element skeleton-line w-40"></div>
        <div className="skeleton-element skeleton-line w-80"></div>
        <div className="skeleton-element skeleton-line w-30"></div>
        <div style={{ height: 12 }}></div>
        <div className="skeleton-element skeleton-line w-40"></div>
      </div>
    </div>
  </li>
);

const EmptyState = () => (
  <div className="empty-state w-100">
    <div className="empty-state-icon">
      <i className="fa-solid fa-user-doctor"></i>
    </div>
    <h3>No Doctors Found</h3>
    <p>Try adjusting your filters or search criteria to discover more healthcare professionals near you.</p>
  </div>
);

/* ── AccSection: identical to FrontClinicList's AccSection ── */
const AccSection = ({ id, label, openSection, setOpenSection, children }) => {
  const isOpen = openSection === id;
  return (
    <div className="cl-acc-item">
      <button
        type="button"
        className={`cl-acc-btn${isOpen ? " is-open" : ""}`}
        onClick={() => setOpenSection(isOpen ? null : id)}
        aria-expanded={isOpen}
      >
        {label}
        <span className="cl-acc-icon">
          <i className="fa-solid fa-chevron-down" />
        </span>
      </button>
      <div className={`cl-acc-panel${isOpen ? " is-open" : ""}`}>
        {children}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   FilterContent — defined OUTSIDE main component (same as
   FrontClinicList) to prevent unmount/remount on every render
   and preserve input focus on keystroke.
───────────────────────────────────────────────────────────── */
const FilterContent = ({
  accordionId,
  openSection,
  setOpenSection,
  /* department */
  filteredDepartments,
  searchDepartment,
  setSearchDepartment,
  selectedDep,
  handleDepChange,
  /* clinic */
  filteredClinic,
  searchClinic,
  setSearchClinic,
  selectedCli,
  handleCliChange,
  /* treatment */
  filteredTreatment,
  searchTreatment,
  setSearchTreatment,
  selectedTreat,
  handleTreatChange,
  /* price */
  handleRangeChange,
  handleRangeAfter,
}) => (
  <>
    {/* Department */}
    <AccSection
      id={`${accordionId}-dept`}
      label="Department"
      openSection={openSection}
      setOpenSection={setOpenSection}
    >
      <div className="cl-filter-search px-0 mx-2">
        <input
          type="text"
          placeholder="Search department…"
          value={searchDepartment}
          onChange={(e) => setSearchDepartment(e.target.value)}
        />
      </div>
      <div className="cl-filter-list px-0">
        {filteredDepartments?.map((cat) => (
          <div
            key={cat.id}
            className="cl-filter-check form-check form-check-inline d-flex align-items-center gap-3 mx-2"
          >
            <input
              type="checkbox"
              value={cat.id}
              checked={selectedDep.includes(String(cat.id))}
              onChange={handleDepChange}
            />
            <label className="form-check-label">
              {cat.name}
            </label>
          </div>
        ))}
      </div>
    </AccSection>

    {/* Clinic */}
    <AccSection
      id={`${accordionId}-clinic`}
      label="Clinic"
      openSection={openSection}
      setOpenSection={setOpenSection}
    >
      <div className="cl-filter-search px-0 mx-2">
        <input
          type="text"
          placeholder="Search clinic…"
          value={searchClinic}
          onChange={(e) => setSearchClinic(e.target.value)}
        />
      </div>
      <div className="cl-filter-list px-0">
        {filteredClinic?.map((cli) => (
          <div
            key={cli.id}
            className="cl-filter-check form-check form-check-inline d-flex align-items-center gap-3 mx-2"
          >
            <input
              type="checkbox"
              value={cli.id}
              checked={selectedCli.includes(String(cli.id))}
              onChange={handleCliChange}
            />
            <label className="form-check-label">
              {cli.name}
            </label>
          </div>
        ))}
      </div>
    </AccSection>

    {/* Treatment — mirrors Clinic above; uses the existing doctor-treatment
        assignment data via the treatments_front / doctorFrontList APIs. */}
    <AccSection
      id={`${accordionId}-treatment`}
      label="Treatment"
      openSection={openSection}
      setOpenSection={setOpenSection}
    >
      <div className="cl-filter-search px-0 mx-2">
        <input
          type="text"
          placeholder="Search treatment…"
          value={searchTreatment}
          onChange={(e) => setSearchTreatment(e.target.value)}
        />
      </div>
      <div className="cl-filter-list px-0">
        {filteredTreatment?.map((t) => (
          <div
            key={t.id}
            className="cl-filter-check form-check form-check-inline d-flex align-items-center gap-3 mx-2"
          >
            <input
              type="checkbox"
              value={t.id}
              checked={selectedTreat.includes(String(t.id))}
              onChange={handleTreatChange}
            />
            <label className="form-check-label">
              {t.name}
            </label>
          </div>
        ))}
      </div>
    </AccSection>

    {/* Price Range */}
    <AccSection
      id={`${accordionId}-price`}
      label="Price Range"
      openSection={openSection}
      setOpenSection={setOpenSection}
    >
      <div className="mx-2 pb-2">
        <PriceRangePicker
          min={0}
          max={200000}
          step={100}
          initialMin={0}
          initialMax={200000}
          currency="₹"
          onChange={handleRangeChange}
          onAfterChange={handleRangeAfter}
        />
      </div>
    </AccSection>
  </>
);

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const FrontDoctorList = () => {

  const [doctors, setDoctors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [doctorcount, setDoctorCount] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revealedPhones, setRevealedPhones] = useState({});

  const [sortBy, setSortBy] = useState("");

  const [department, setDepartment] = useState([]);
  const [searchDepartment, setSearchDepartment] = useState("");
  const [selectedDep, setSelectedDep] = useState([]);

  const filteredDepartments = department?.filter((cat) =>
    cat.name.toLowerCase().includes(searchDepartment.toLowerCase())
  );

  const sortOptions = [
    { label: "Alphabetically, A-Z", value: "az" },
    { label: "Alphabetically, Z-A", value: "za" },
    { label: "Price, low to high", value: "price_low_high" },
    { label: "Price, high to low", value: "price_high_low" },
  ];

  const tamilNaduCities = [
    "Chennai", "Coimbatore", "Madurai", "Trichy", "Salem", "Tirunelveli", "Erode",
    "Vellore", "Thoothukudi", "Dindigul", "Thanjavur", "Ranipet", "Sivakasi", "Karur",
    "Udhagamandalam", "Hosur", "Nagercoil", "Kanchipuram", "Kumarapalayam", "Karaikudi",
    "Neyveli", "Cuddalore", "Kumbakonam", "Tiruvannamalai", "Pollachi", "Rajapalayam",
    "Gudiyatham", "Pudukkottai", "Vaniyambadi", "Ambur", "Nagapattinam", "Viluppuram",
  ];
  const cityOptions = tamilNaduCities.map((city) => ({ value: city, label: city }));

  const fetchDepartmentData = () => {
    fetch(`${API_BASE_URL}api/departent_front`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.data;
        setDepartment(list || []);
      })
      .catch((err) => console.log(err));
  };

  const [clinic, setClinic] = useState([]);
  const [searchClinic, setSearchClinic] = useState("");
  const [selectedCli, setSelectedCli] = useState([]);

  const filteredClinic = clinic?.filter((cat) =>
    cat.name.toLowerCase().includes(searchClinic.toLowerCase())
  );

  const fetchClinicData = () => {
    fetch(`${API_BASE_URL}api/clinic_front`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.data;
        setClinic(list || []);
      })
      .catch((err) => console.log(err));
  };

  /* Treatment filter — same pattern as Clinic above. This filter didn't
     exist in the UI at all before; added to match the Clinic filter 1:1. */
  const [treatment, setTreatment] = useState([]);
  const [searchTreatment, setSearchTreatment] = useState("");
  const [selectedTreat, setSelectedTreat] = useState([]);

  const filteredTreatment = treatment?.filter((t) =>
    t.name.toLowerCase().includes(searchTreatment.toLowerCase())
  );

  const fetchTreatmentData = () => {
    fetch(`${API_BASE_URL}api/treatments_front`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.data;
        setTreatment(list || []);
      })
      .catch((err) => console.log(err));
  };

  const [lastPage, setLastPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [searchName, setSearchName] = useState("");
  const [city, setCity] = useState("");

  /* Mobile drawer / sort sheet state — same pattern as FrontClinicList */
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [openSection, setOpenSection] = useState("sidebar-dept");

  const fetchDoctors = async (
    page = 1,
    min = 0,
    max = 10000,
    sortby = "",
    departmentsearch = [],
    clinicIds = [],
    name = "",
    cityVal = "",
    treatmentIds = []
  ) => {
    setIsLoading(true);
    const departmentParm = departmentsearch.join(",");
    const clinicParm = clinicIds.join(",");
    const treatmentParm = treatmentIds.join(",");
    const response = await fetch(
      `${API_BASE_URL}api/doctorFrontList?minPrice=${min}&maxPrice=${max}` +
      `&clinics=${clinicParm}&deparments=${departmentParm}` +
      `&treatments=${treatmentParm}` +
      `&sortby=${sortby}&page=${page}` +
      `&name=${encodeURIComponent(name)}&city=${encodeURIComponent(cityVal)}`
    );
    const json = await response.json();
    if (json.status) {
      setTotalPages(json.data.last_page);
      setDoctorCount(json.data.total);
      setDoctors(json.data.data);
      setCurrentPage(json.data.current_page);
      setLastPage(json.data.last_page);
    }
    setIsLoading(false);
  };

  // BUGFIX: this effect was commented out, so typing in the search box only
  // ever updated `searchName` state — it never re-fetched doctors, which is
  // why the search box appeared to do nothing. Re-enabled, debounced, and
  // now carries every other currently-active filter along with it (the old
  // version below would have reset price/department/clinic/treatment back
  // to their defaults on every keystroke, silently dropping any other
  // filter the visitor had already applied).
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchDoctors(1, minPrice, maxPrice, sortBy, selectedDep, selectedCli, searchName, city, selectedTreat);
    }, 500);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName]);

  useEffect(() => {
    fetchDoctors(1);
    fetchDepartmentData();
    fetchClinicData();
    fetchTreatmentData();
  }, []);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }

  const handleRangeChange = ([min, max]) => { console.log("live", min, max); };

  const handleRangeAfter = ([min, max]) => {
    setMinPrice(min);
    setMaxPrice(max);
    fetchDoctors(1, min, max, sortBy, selectedDep, selectedCli, searchName, city, selectedTreat);
  };

  const togglePhone = (doctorId) => {
    setRevealedPhones(prev => ({ ...prev, [doctorId]: !prev[doctorId] }));
  };

  /* Department checkbox handler */
  const handleDepChange = (e) => {
    const value = e.target.value;
    setSelectedDep((prev) => {
      const updated = e.target.checked
        ? [...prev, value]
        : prev.filter((id) => id !== value);
      fetchDoctors(1, minPrice, maxPrice, sortBy, updated, selectedCli, searchName, city, selectedTreat);
      return updated;
    });
  };

  /* Clinic checkbox handler */
  const handleCliChange = (e) => {
    const value = e.target.value;
    setSelectedCli((prev) => {
      const updated = e.target.checked
        ? [...prev, value]
        : prev.filter((id) => id !== value);
      fetchDoctors(1, minPrice, maxPrice, sortBy, selectedDep, updated, searchName, city, selectedTreat);
      return updated;
    });
  };

  /* Treatment checkbox handler — same pattern as Clinic above */
  const handleTreatChange = (e) => {
    const value = e.target.value;
    setSelectedTreat((prev) => {
      const updated = e.target.checked
        ? [...prev, value]
        : prev.filter((id) => id !== value);
      fetchDoctors(1, minPrice, maxPrice, sortBy, selectedDep, selectedCli, searchName, city, updated);
      return updated;
    });
  };

  /* Clear / Reset */
  const handleResetFilter = () => {
    setSelectedDep([]);
    setSelectedCli([]);
    setSelectedTreat([]);
    setMinPrice(0);
    setMaxPrice(10000);
    setSearchDepartment("");
    setSearchClinic("");
    setSearchTreatment("");
    fetchDoctors(1, 0, 10000, sortBy, [], [], searchName, city, []);
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    setSortBy("");
    setSearchName("");
    setCity("");
    handleResetFilter();
    fetchDoctors(1, 0, 10000, "", [], [], "", "", []);
  };

  const openFilter = () => setFilterOpen(true);
  const closeFilter = () => setFilterOpen(false);
  const openSort = () => setSortOpen(true);
  const closeSort = () => setSortOpen(false);

  /* Shared props passed to both sidebar and mobile drawer FilterContent */
  const filterContentProps = {
    openSection,
    setOpenSection,
    filteredDepartments,
    searchDepartment,
    setSearchDepartment,
    selectedDep,
    handleDepChange,
    filteredClinic,
    searchClinic,
    setSearchClinic,
    selectedCli,
    handleCliChange,
    filteredTreatment,
    searchTreatment,
    setSearchTreatment,
    selectedTreat,
    handleTreatChange,
    handleRangeChange,
    handleRangeAfter,
  };

  const activeFilterCount =
    selectedDep.length +
    selectedCli.length +
    selectedTreat.length +
    (minPrice > 0 || maxPrice < 10000 ? 1 : 0);

  return (
    <>
      <Header />

      <div className="doctor-list-page cl-page">

        {/* ── Breadcrumb ── */}
        <section className="premium-breadcrumb cl-breadcrumb-bar">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li className="breadcrumb-item active" aria-current="page">Doctor List</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </section>

        {/* ── Listing section ── */}
        <section className="product-list section-padding pt-0 doctor-list cl-listing-section mt-3">
          <div className="container-fluid px-lg-5">
            <div className="row product-listing-row cl-layout">

              {/* ═══════════════════════════════════════════
                  DESKTOP SIDEBAR — matches FrontClinicList
              ═══════════════════════════════════════════ */}
              <aside className="filter-web left-filter-blade sidebar-filter-block cl-sidebar px-0 pb-0">
                <div className="cl-filter-head sidebarfilter-menu-head py-0">
                  <span className="cl-filter-head-title filter-heading">Filters</span>
                  <button
                    type="button"
                    className="cl-filter-clear filter-clear my-2"
                    onClick={handleClearAll}
                  >
                    Clear All
                  </button>
                </div>

                {/* Same FilterContent used in FrontClinicList sidebar */}
                <FilterContent accordionId="sidebar" {...filterContentProps} />
              </aside>

              {/* ═══════════════════════════════════════════
                  MAIN CONTENT — search + cards
              ═══════════════════════════════════════════ */}
              <div className="filter_options cl-main">

                {/* Search bar */}
                <div className="doctor-filter-search cl-search-bar">
                  <div className="row g-2 align-items-center">
                    <div className="col-md-4 col-xl-3">
                      <Select
                        className="form-select-1 cl-city-select"
                        options={cityOptions}
                        placeholder="Select City"
                        onChange={(selected) => {
                          const val = selected?.value || "";
                          setCity(val);
                          fetchDoctors(1, minPrice, maxPrice, sortBy, selectedDep, selectedCli, searchName, val, selectedTreat);
                        }}
                        isClearable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 1 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                    <div className="col-md-8 col-xl-9">
                      <div className="search-block">
                        <div className="autocomplete-container">
                          <div className="cl-search-wrap search-block ">
                            <i className="fa-solid fa-search search-icon cl-search-icon"></i>
                            <input
                              type="text"
                              placeholder="Search doctor name"
                              value={searchName}
                              id="searchInput"
                              autoComplete="off"
                              onChange={(e) => setSearchName(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results header + desktop sort */}
                <div className="cl-results-header">
                  <div>
                    <h4 className="cl-results-title">Doctor List</h4>
                    <p className="cl-results-count showing_items_count mb-0">
                      Showing <strong>{doctorcount}</strong> results
                    </p>
                  </div>
                  <div className="d-lg-block d-none sort-dropdown dropdown">
                    <select
                      className="form-select cl-sort-select"
                      value={sortBy}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSortBy(value);
                        fetchDoctors(1, minPrice, maxPrice, value, selectedDep, selectedCli, searchName, city, selectedTreat);
                      }}
                    >
                      <option value="">Sort By</option>
                      <option value="latest">Latest</option>
                      <option value="az">Alphabetically, A–Z</option>
                      <option value="za">Alphabetically, Z–A</option>
                      <option value="price_low_high">Price, low to high</option>
                      <option value="price_high_low">Price, high to low</option>
                    </select>
                  </div>
                </div>

                {/* ── Doctor cards ── */}
                <ul className="doctor-card">
                  {isLoading ? (
                    <>
                      <SkeletonCard />
                      <SkeletonCard />
                      <SkeletonCard />
                    </>
                  ) : doctors.length > 0 ? (
                    doctors.map((doctor) => (
                      <li className="category-block" key={doctor.id}>
                        <div className="category-card mt-0 mb-0">
                          <div className="w-100">
                            <div className="flex-shrink-0">
                              <div className="d-flex justify-content-start align-items-start flex-wrap flex-md-nowrap gap-md-3">
                                <Link to={`/doctordetails/${doctor.slug}`} className="flex-shrink-0">
                                  <img className="rounded-pill doc-img"
                                    src={
                                        doctor.images
                                          ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                                          : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`
                                      }
                                    alt={doctor.name}
                                  />
                                </Link>
                                <div className="w-100">
                                  <div className="d-flex align-items-center gap-3">
                                    <Link to={`/doctordetails/${doctor.slug}`}>
                                      <h3 className="doctor-name mb-0">{doctor?.name}</h3>
                                    </Link>
                                    <span className="badge d-inline">{doctor?.status}</span>
                                  </div>
                                  <div className="doctor-designation h-unset">{doctor?.department_title?.name}</div>
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="doctor-designation mb-0 h-unset">{doctor?.experience}</div>
                                    <div className="verified m-0">
                                      <i className="fa-solid fa-circle-check"></i> Verified
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex flex-column">
                              <div className="doctor-location">
                                <div className="doctor-designation d-flex flex-wrap gap-2 align-items-center location">
                                  {doctor?.clinics?.map((clinicItem, index) => (
                                    <div className="w-100" key={index}>
                                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinicItem.address || "")}`} target="_blank" className="w-100 d-block">
                                        <i className="fa fa-location-dot mb-0"></i>
                                        {doctor.address},{doctor.city} - {doctor.pincode}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="doctor-rating mb-3 d-flex align-items-center">
                                <div className="stars">
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <i key={n} className={n <= Math.round(getDoctorRatingSummary(doctor).average) ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                                  ))}
                                </div>
                                <div className="score">
                                  {getDoctorRatingSummary(doctor).count ? `(${getDoctorRatingSummary(doctor).average.toFixed(1)}/5 · ${getDoctorRatingSummary(doctor).count})` : "(No reviews yet)"}
                                </div>
                              </div>
                              <div className="d-flex align-items-stretch flex-wrap gap-3 contact-btn justify-content-between">
                                <Link to={`/doctordetails/${doctor.slug}`} className="dc-book-btn book-btn d-flex align-items-center justify-content-center w-100"
                                  style={{ padding: '6px 14px', fontSize: '13px', minHeight: '36px', width: '100% !important' }}
                                >
                                  <span style={{ background: "none", height: "unset", width: "unset" }}>Make an Appointment</span>
                                </Link>
                                <button
                                  type="button"
                                  className="btn btn-main btn-css-1 book-btn rounded-pill d-flex align-items-center justify-content-center"
                                  style={{ padding: '6px 14px', fontSize: '13px', minHeight: '36px', width: '100%' }}
                                  onClick={() => togglePhone(doctor.id)}
                                >
                                  Contact Clinic
                                </button>
                              </div>
                              <div className="doctor-designation doctor-fees my-3 h-unset">
                                {doctor?.videocall_price == 0
                                  ? "Zero Booking Fee"
                                  : `₹ ${doctor?.videocall_price} Consultation fee at clinic`}
                              </div>
                            </div>
                          </div>

                          {/* Phone reveal */}
                          <div className={`content-section-1 mt-0 w-100 tab-section-1 ${revealedPhones[doctor.id] ? 'phone-revealed' : 'phone-hidden'}`}>
                            {revealedPhones[doctor.id] ? (
                              <>
                                <p className="mb-0 d-flex"><i className="fa-solid fa-phone" style={{ marginRight: 6 }}></i> Phone number</p>
                                <Link
                                  to={`tel:${doctor?.clinics?.[0]?.mobile || ""}`}
                                  className="phone-number-value"
                                >
                                  {doctor?.clinics?.[0]?.mobile || "No Phone"}
                                </Link>
                                {/* <Link to={`tel:${doctor?.phone}`} className="phone-number-value">{doctor?.phone}</Link> */}
                              </>
                            ) : (
                              <button className="reveal-phone-btn" onClick={() => togglePhone(doctor.id)}>
                                <i className="fa-solid fa-phone" style={{ marginRight: 6 }}></i> Click to reveal phone number
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <EmptyState />
                  )}
                </ul>

                <div className="cl-pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) =>
                      fetchDoctors(page, minPrice, maxPrice, sortBy, selectedDep, selectedCli, searchName, city, selectedTreat)
                    }
                  />
                </div>

              </div>
              {/* end cl-main */}

            </div>
          </div>
        </section>

      </div>

      {/* ═══════════════════════════════════════════
          MOBILE BOTTOM BAR — same as FrontClinicList
      ═══════════════════════════════════════════ */}
      <div className="cl-mobile-bar mobile_filters mobile-filter">
        <button className="cl-mobile-bar-btn canvas-btn rounded-pill" onClick={openFilter}>
          <i className="fa-solid fa-filter" /> Filters
          {activeFilterCount > 0 && (
            <span style={{
              marginLeft: 5, fontSize: 11, fontWeight: 600,
              background: "#e6f7f5", color: "#1a9e8f",
              borderRadius: 20, padding: "1px 7px",
            }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <button className="pl-mob-btn pl-mob-btn--filter rounded-pill" onClick={openSort}>
          <i className="fa-solid fa-up-down" /> Sort By
        </button>
      </div>

      {/* Overlay */}
      <div
        className={`cl-overlay${filterOpen || sortOpen ? " is-visible is-open" : ""}`}
        onClick={() => { closeFilter(); closeSort(); }}
        aria-hidden="true"
      />

      {/* Filter drawer */}
      <div className={`cl-drawer${filterOpen ? " is-open" : ""}`} tabIndex="-1" aria-labelledby="drDrawerLabel">
        <div className="cl-drawer-header">
          <h5 className="cl-drawer-title" id="drDrawerLabel">Filters</h5>
          <button className="cl-drawer-close" type="button" onClick={closeFilter} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="cl-drawer-body">
          <FilterContent accordionId="drawer" {...filterContentProps} />
        </div>
        <div className="cl-drawer-footer sidebarfilter_menu_footer">
          <button className="btn-reset btn btn_cancel" type="button" onClick={handleResetFilter}>Reset</button>
          <button className="btn-apply btn btn_apply" type="button" onClick={closeFilter}>Apply Filters</button>
        </div>
      </div>

      {/* Sort bottom sheet */}
      <div className={`cl-sort-sheet${sortOpen ? " is-open" : ""}`} tabIndex="-1" aria-labelledby="drSortLabel">
        <div className="cl-sort-handle" />
        <div className="cl-sort-sheet-header">
          <h5 className="cl-sort-sheet-title" id="drSortLabel">Sort by</h5>
          <button className="cl-drawer-close" type="button" onClick={closeSort} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="cl-sort-sheet-body">
          <div className="sort_by_list">
            {sortOptions.map((option) => (
              <div className="cl-sort-option form-check mb-2 border-0" key={option.value}>
                <input
                  className="sort-by form-check-input"
                  type="radio"
                  name="drSortBy"
                  id={`dr_${option.value}`}
                  value={option.value}
                  checked={sortBy === option.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSortBy(value);
                    fetchDoctors(1, minPrice, maxPrice, value, selectedDep, selectedCli, searchName, city, selectedTreat);
                    closeSort();
                  }}
                />
                <label className="form-check-label" htmlFor={`dr_${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FrontDoctorList;